import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import { heroData, fragranceSeries, skincareSeries, featuredArticles, faqData, statsData, brandStory } from "@/mocks/homeData";

export default function HomePage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [subscribeError, setSubscribeError] = useState("");

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubscribeStatus("success");
      setEmail("");
      return;
    }
    setSubscribeStatus("submitting");
    setSubscribeError("");
    try {
      const formData = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9s9a9fnahb7382ovshg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string })?.code === "OK") {
        setSubscribeStatus("success");
        setEmail("");
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || text
          || "订阅失败，请稍后再试";
        setSubscribeError(String(msg).includes("spam") ? "提交失败，请稍后再试" : String(msg));
        setSubscribeStatus("error");
      }
    } catch {
      setSubscribeError("网络错误，请检查连接后重试");
      setSubscribeStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20oriental%20fragrance%20brand%20atmosphere%20with%20rose%20petals%20and%20silk%20fabric%20draped%20over%20dark%20wood%2C%20warm%20golden%20and%20deep%20crimson%20tones%2C%20mysterious%20and%20sensual%20ambience%2C%20artistic%20abstract%20composition%2C%20editorial%20photography%20style&width=1920&height=1080&seq=home-hero-main&orientation=landscape"
          alt="媚肉之香 东方香氛美学"
          className="absolute inset-0 w-full h-full object-cover"
          title="媚肉之香 感官香氛品牌"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55"></div>
        <div className="relative z-10 w-full px-6 md:px-10 text-center">
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold text-background-50 mb-4 tracking-wide">
            {heroData.title}
          </h1>
          <p className="font-heading text-xl md:text-2xl text-background-100/90 mb-4">
            {heroData.subtitle}
          </p>
          <p className="text-sm md:text-base text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
            {heroData.description}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={heroData.ctaPrimaryLink}
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {heroData.ctaPrimary}
            </Link>
            <Link
              to={heroData.ctaSecondaryLink}
              className="px-8 py-3 border border-background-50/40 text-background-50 rounded-full font-medium text-sm hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {heroData.ctaSecondary}
            </Link>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-background-50/40 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-background-50/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-primary-600">
        <div className="w-full px-6 md:px-10 py-8">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {statsData.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-background-50">{stat.value}</p>
                <p className="text-sm text-primary-200 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-50">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
              {brandStory.title}
            </h2>
            <p className="text-sm text-primary-600 font-medium mb-6">{brandStory.subtitle}</p>
            {brandStory.paragraphs.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {p}
              </p>
            ))}
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              了解更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20oriental%20perfume%20making%20workshop%20with%20natural%20ingredients%20and%20glass%20bottles%20on%20wooden%20table%2C%20warm%20candlelight%20ambiance%2C%20artisan%20craftsmanship%20aesthetic%2C%20soft%20focus%20background%2C%20editorial%20photography&width=800&height=1000&seq=brand-story-image&orientation=portrait"
              alt="媚肉之香品牌故事"
              className="w-full h-full object-cover object-top"
              title="媚肉之香 东方香氛品牌故事"
            />
          </div>
        </div>
      </section>

      {/* Fragrance Series */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
              香氛系列
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              每一款香氛都是一首用气味谱写的诗，等待与你的肌肤相遇
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-product-shop>
            {fragranceSeries.map((item) => (
              <ProductCard key={item.id} item={item} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/category/fragrance"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
            >
              查看全部香氛系列 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Skincare Series */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
              肌肤养护
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              以香养肤，让每一次护肤都成为感官的盛宴
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-product-shop>
            {skincareSeries.map((item) => (
              <ProductCard key={item.id} item={item} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/category/skincare"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
            >
              查看全部肌肤养护 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
                香氛文章
              </h2>
              <p className="text-sm text-foreground-500">探索香氛世界的更多故事</p>
            </div>
            <Link
              to="/category/articles"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              查看全部文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    title={article.title}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">关于媚肉之香的更多细节，在这里找到答案</p>
          </div>
          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-background-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                >
                  <span className="font-medium text-sm md:text-base text-foreground-800 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaqIndex === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? "max-h-96 pb-4" : "max-h-0"
                  }`}
                >
                  <p className="px-6 text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-accent-400/20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
            订阅我们的香氛资讯
          </h2>
          <p className="text-sm text-foreground-500 mb-8">
            第一时间获取新品发布、限定活动和香氛知识分享
          </p>
          <form
            onSubmit={handleSubscribe}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <div className="anti-spam-wrapper">
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入您的邮箱地址"
              required
              className="flex-1 px-5 py-3 bg-background-50 border border-background-300/60 rounded-full text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
            />
            <button
              type="submit"
              disabled={subscribeStatus === "submitting"}
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {subscribeStatus === "submitting" ? "提交中..." : "订阅"}
            </button>
          </form>
          {subscribeStatus === "success" && (
            <p className="mt-4 text-sm text-accent-700 font-medium">订阅成功！感谢您的关注。</p>
          )}
          {subscribeStatus === "error" && (
            <p className="mt-4 text-sm text-primary-600 font-medium">{subscribeError}</p>
          )}
        </div>
      </section>

      <Footer />

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://meirouzhixiang.com/#organization",
                name: "媚肉之香",
                url: "https://meirouzhixiang.com",
                description: "融合东方香氛美学与肌肤养护精髓的高端感官体验品牌",
                contactPoint: {
                  "@type": "ContactPoint",
                  telephone: "0571-8888-6666",
                  contactType: "customer service",
                  availableLanguage: ["Chinese"],
                },
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "灵隐路18号",
                  addressLocality: "杭州",
                  addressRegion: "浙江",
                  addressCountry: "CN",
                },
              },
              {
                "@type": "WebSite",
                "@id": "https://meirouzhixiang.com/#website",
                url: "https://meirouzhixiang.com",
                name: "媚肉之香",
                inLanguage: "zh-CN",
              },
              {
                "@type": "WebPage",
                "@id": "https://meirouzhixiang.com/#webpage",
                url: "https://meirouzhixiang.com",
                name: "媚肉之香——感官体验落地页",
                description: "融合东方香氛美学与肌肤养护精髓，唤醒内在魅力与自信",
                isPartOf: { "@id": "https://meirouzhixiang.com/#website" },
              },
              {
                "@type": "FAQPage",
                "@id": "https://meirouzhixiang.com/#faq",
                mainEntity: faqData.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                  },
                })),
              },
            ],
          }),
        }}
      />
    </>
  );
}

function ProductCard({ item }: { item: { id: string; name: string; tag: string; description: string; image: string; price: string; volume: string } }) {
  return (
    <Link
      to={`/category/${item.id.startsWith("frag") ? "fragrance" : "skincare"}`}
      className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          title={item.name}
        />
        {item.tag && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
            {item.tag}
          </span>
        )}
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
            {item.name}
          </h3>
          <span className="text-xs text-foreground-400 whitespace-nowrap mt-1">{item.volume}</span>
        </div>
        <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">{item.description}</p>
        <p className="font-heading text-lg font-semibold text-primary-600">{item.price}</p>
      </div>
    </Link>
  );
}